/**
 * BroadcastChannel coordinator for shared WebSocket connection across tabs
 * Only one tab (leader) maintains the actual WebSocket connection
 * Other tabs communicate through BroadcastChannel
 */

import { wsLogger } from '@/shared/lib/logger';

export type BroadcastMessage =
  | { type: 'ping'; tabId: string; timestamp: number }
  | { type: 'pong'; tabId: string; timestamp: number }
  | { type: 'leader-announce'; tabId: string; timestamp: number }
  | { type: 'leader-resign'; tabId: string; timestamp: number }
  | { type: 'websocket-message'; channel: string; event: string; data: unknown }
  | { type: 'connection-status'; status: string; isConnected: boolean }
  | { type: 'request-status'; tabId: string };

const LEADER_PING_INTERVAL = 5000; // 5 seconds
const LEADER_TIMEOUT = 10000; // 10 seconds - if no ping from leader, elect new one
const CHANNEL_NAME = 'getfacade-websocket';

export class BroadcastCoordinator {
  private channel: BroadcastChannel | null = null;
  private tabId: string;
  private isLeader: boolean = false;
  private leaderTabId: string | null = null;
  private lastLeaderPing: number = 0;
  private pingInterval: NodeJS.Timeout | null = null;
  private checkInterval: NodeJS.Timeout | null = null;
  private electionTimeout: ReturnType<typeof setTimeout> | null = null;
  private reElectionTimeout: ReturnType<typeof setTimeout> | null = null;
  private messageHandlers: Map<string, (message: BroadcastMessage) => void> =
    new Map();

  constructor() {
    this.tabId = `tab-${Date.now()}-${Math.random().toString(36).substring(7)}`;
  }

  /**
   * Initialize the broadcast coordinator
   */
  initialize(): void {
    if (typeof window === 'undefined' || !('BroadcastChannel' in window)) {
      wsLogger.warn(
        'BroadcastChannel not supported - each tab will have own connection'
      );
      // Fallback: act as leader (each tab is independent)
      this.isLeader = true;
      this.leaderTabId = this.tabId;
      return;
    }

    try {
      this.channel = new BroadcastChannel(CHANNEL_NAME);
      this.channel.onmessage = this.handleMessage.bind(this);

      // wsLogger.info(
      //   `BroadcastCoordinator initialized with tabId: ${this.tabId}`
      // );

      // Start leader election process
      this.startLeaderElection();

      // Periodically check leader health
      this.checkInterval = setInterval(() => {
        this.checkLeaderHealth();
      }, 2000);
    } catch (_error) {
      // wsLogger.error('Failed to initialize BroadcastChannel:', _error);
      // Fallback: act as leader
      this.isLeader = true;
      this.leaderTabId = this.tabId;
    }
  }

  /**
   * Start the leader election process
   */
  private startLeaderElection(): void {
    // wsLogger.info('Starting leader election...');

    // Request status from existing tabs
    this.broadcast({
      type: 'request-status',
      tabId: this.tabId,
    });

    // If no response within 1 second, become leader
    this.electionTimeout = setTimeout(() => {
      this.electionTimeout = null;
      if (!this.leaderTabId) {
        this.becomeLeader();
      }
    }, 1000);
  }

  /**
   * Become the leader tab
   */
  private becomeLeader(): void {
    if (this.isLeader) return;

    // wsLogger.info(`Tab ${this.tabId} becoming leader`);
    this.isLeader = true;
    this.leaderTabId = this.tabId;
    this.lastLeaderPing = Date.now();

    // Announce leadership
    this.broadcast({
      type: 'leader-announce',
      tabId: this.tabId,
      timestamp: Date.now(),
    });

    // Start sending pings
    this.startLeaderPing();

    // Notify handlers about leadership change
    this.notifyLeadershipChange(true);
  }

  /**
   * Resign from leadership
   */
  private resignLeadership(): void {
    if (!this.isLeader) return;

    // wsLogger.info(`Tab ${this.tabId} resigning leadership`);

    // Announce resignation
    this.broadcast({
      type: 'leader-resign',
      tabId: this.tabId,
      timestamp: Date.now(),
    });

    this.isLeader = false;
    this.leaderTabId = null;
    this.stopLeaderPing();

    // Notify handlers about leadership change
    this.notifyLeadershipChange(false);
  }

  /**
   * Start sending leader pings
   */
  private startLeaderPing(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
    }

    this.pingInterval = setInterval(() => {
      if (this.isLeader) {
        this.broadcast({
          type: 'ping',
          tabId: this.tabId,
          timestamp: Date.now(),
        });
      }
    }, LEADER_PING_INTERVAL);
  }

  /**
   * Stop sending leader pings
   */
  private stopLeaderPing(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  /**
   * Check if leader is still alive
   */
  private checkLeaderHealth(): void {
    // If we're the leader, we're healthy by definition
    if (this.isLeader) return;

    // If we don't have a leader, try to become one
    if (!this.leaderTabId) {
      this.becomeLeader();
      return;
    }

    // Check if leader has timed out
    const timeSinceLastPing = Date.now() - this.lastLeaderPing;
    if (timeSinceLastPing > LEADER_TIMEOUT) {
      wsLogger.warn(
        `Leader timeout detected (${timeSinceLastPing}ms) - starting new election`
      );
      this.leaderTabId = null;
      this.becomeLeader();
    }
  }

  /**
   * Handle incoming broadcast messages
   */
  private handleMessage(event: MessageEvent<BroadcastMessage>): void {
    const message = event.data;

    // Ignore our own messages
    if ('tabId' in message && message.tabId === this.tabId) {
      return;
    }

    switch (message.type) {
      case 'ping':
        // Update last leader ping time
        if (message.tabId === this.leaderTabId) {
          this.lastLeaderPing = Date.now();
        }
        break;

      case 'pong':
        // Response to our ping (not used currently)
        break;

      case 'leader-announce':
        // Another tab announced leadership
        if (!this.isLeader || message.timestamp > Date.now() - 2000) {
          // wsLogger.info(`Acknowledging leader: ${message.tabId}`);
          this.leaderTabId = message.tabId;
          this.lastLeaderPing = Date.now();

          // If we were leader, resign
          if (this.isLeader) {
            this.resignLeadership();
          }
        }
        break;

      case 'leader-resign':
        // Leader resigned, start election if it was our leader
        if (message.tabId === this.leaderTabId) {
          // wsLogger.info('Leader resigned - starting election');
          this.leaderTabId = null;
          // Wait a bit to avoid race conditions
          this.reElectionTimeout = setTimeout(
            () => {
              this.reElectionTimeout = null;
              if (!this.leaderTabId) {
                this.becomeLeader();
              }
            },
            100 + Math.random() * 200
          );
        }
        break;

      case 'request-status':
        // Another tab wants to know who's leader
        if (this.isLeader) {
          this.broadcast({
            type: 'leader-announce',
            tabId: this.tabId,
            timestamp: Date.now(),
          });
        }
        break;

      case 'websocket-message':
      case 'connection-status':
        // Forward to registered handlers
        this.notifyMessageHandlers(message);
        break;
    }
  }

  /**
   * Broadcast a message to all tabs
   */
  private broadcast(message: BroadcastMessage): void {
    if (!this.channel) return;

    try {
      this.channel.postMessage(message);
    } catch (error) {
      wsLogger.error('Failed to broadcast message:', error);
    }
  }

  /**
   * Broadcast WebSocket message to all tabs (called by leader)
   */
  broadcastWebSocketMessage(
    channel: string,
    event: string,
    data: unknown
  ): void {
    if (!this.isLeader) return;

    this.broadcast({
      type: 'websocket-message',
      channel,
      event,
      data,
    });
  }

  /**
   * Broadcast connection status to all tabs (called by leader)
   */
  broadcastConnectionStatus(status: string, isConnected: boolean): void {
    if (!this.isLeader) return;

    this.broadcast({
      type: 'connection-status',
      status,
      isConnected,
    });
  }

  /**
   * Register a message handler
   */
  onMessage(
    handlerId: string,
    handler: (message: BroadcastMessage) => void
  ): void {
    this.messageHandlers.set(handlerId, handler);
  }

  /**
   * Unregister a message handler
   */
  offMessage(handlerId: string): void {
    this.messageHandlers.delete(handlerId);
  }

  /**
   * Notify all message handlers
   */
  private notifyMessageHandlers(message: BroadcastMessage): void {
    this.messageHandlers.forEach((handler) => {
      try {
        handler(message);
      } catch (error) {
        wsLogger.error('Error in message handler:', error);
      }
    });
  }

  /**
   * Notify about leadership change
   */
  private notifyLeadershipChange(_isLeader: boolean): void {
    // Notify through special message
    this.messageHandlers.forEach((handler) => {
      try {
        handler({
          type: 'leader-announce',
          tabId: this.tabId,
          timestamp: Date.now(),
        });
      } catch (error) {
        wsLogger.error('Error notifying leadership change:', error);
      }
    });
  }

  /**
   * Check if this tab is the leader
   */
  getIsLeader(): boolean {
    return this.isLeader;
  }

  /**
   * Get the leader tab ID
   */
  getLeaderTabId(): string | null {
    return this.leaderTabId;
  }

  /**
   * Get this tab's ID
   */
  getTabId(): string {
    return this.tabId;
  }

  /**
   * Cleanup coordinator
   */
  destroy(): void {
    // wsLogger.info(`Destroying BroadcastCoordinator for tab ${this.tabId}`);

    // Resign leadership if we're leader
    if (this.isLeader) {
      this.resignLeadership();
    }

    // Clear intervals and pending timeouts
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
    }
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }
    if (this.electionTimeout) {
      clearTimeout(this.electionTimeout);
      this.electionTimeout = null;
    }
    if (this.reElectionTimeout) {
      clearTimeout(this.reElectionTimeout);
      this.reElectionTimeout = null;
    }

    // Close channel
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }

    // Clear handlers
    this.messageHandlers.clear();
  }
}

// Singleton instance
let coordinatorInstance: BroadcastCoordinator | null = null;

/**
 * Get or create the broadcast coordinator singleton
 */
export function getBroadcastCoordinator(): BroadcastCoordinator {
  if (!coordinatorInstance) {
    coordinatorInstance = new BroadcastCoordinator();
    coordinatorInstance.initialize();
  }
  return coordinatorInstance;
}

/**
 * Destroy the coordinator singleton
 */
export function destroyBroadcastCoordinator(): void {
  if (coordinatorInstance) {
    coordinatorInstance.destroy();
    coordinatorInstance = null;
  }
}
